// One direct link to one peer (C-link, W3b): an RTCPeerConnection with channels `ctl` (reliable, ordered) and `ride` (unordered, no retransmits), signaled by the caller over Ably `link_*`. The remote offers, the runner answers.

const ICE_SERVERS = [{ urls: ['stun:stun.l.google.com:19302'] }]
const CHANNELS = { ctl: { ordered: true }, ride: { ordered: false, maxRetransmits: 0 } }

/**
 * `onSignal(kind, data)` — 'offer'/'answer' with an SDP string, 'ice' with a candidate (null = end) — for the caller to publish;
 * `onMessage(msg, label)` per inbound frame; `onOpen()` once `ctl` opens; `onClose()` once, whatever ended it.
 */
export const createPeer = ({ initiator, onSignal, onMessage, onOpen, onClose, logger }) => {
	const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
	const channels = {}
	let closed = false
	// Signaling steps run in arrival order: a candidate waits for the description it belongs to.
	let chain = Promise.resolve()

	const close = () => {
		if (closed) return
		closed = true
		Object.values(channels).forEach(ch => ch.close())
		pc.close()
		onClose()
	}

	const adopt = ch => {
		if (!CHANNELS[ch.label]) return
		channels[ch.label] = ch
		ch.onmessage = ({ data }) => {
			let msg
			try {
				msg = JSON.parse(data)
			} catch {
				return
			}
			onMessage(msg, ch.label)
		}
		if (ch.label === 'ctl') {
			ch.onopen = () => !closed && onOpen()
			ch.onclose = close
		}
	}

	pc.onicecandidate = ({ candidate }) => onSignal('ice', candidate ? candidate.toJSON() : null)
	pc.onconnectionstatechange = () => {
		if (pc.connectionState === 'failed' || pc.connectionState === 'closed') close()
	}
	pc.ondatachannel = ({ channel }) => adopt(channel)

	const step = (label, fn) => {
		chain = chain
			.then(() => !closed && fn())
			.catch(error => {
				logger?.warn(`p2p ${label} failed`, error?.message)
				close()
			})
	}

	if (initiator) {
		Object.entries(CHANNELS).forEach(([label, opts]) => adopt(pc.createDataChannel(label, opts)))
		step('offer', async () => {
			await pc.setLocalDescription(await pc.createOffer())
			onSignal('offer', pc.localDescription.sdp)
		})
	}

	// Candidates that beat their description wait for it. One bad candidate is skipped, not fatal.
	const pending = []
	const addCandidate = c =>
		pc.addIceCandidate(c).catch(error => logger?.warn('p2p candidate skipped', error?.message))
	const setRemote = async desc => {
		await pc.setRemoteDescription(desc)
		for (const c of pending.splice(0)) await addCandidate(c)
	}

	/** An inbound `link_*`: kind 'offer' | 'answer' | 'ice'. */
	const signal = (kind, data) => {
		if (kind === 'offer')
			step('answer', async () => {
				await setRemote({ type: 'offer', sdp: data })
				await pc.setLocalDescription(await pc.createAnswer())
				onSignal('answer', pc.localDescription.sdp)
			})
		else if (kind === 'answer') step('answer', () => setRemote({ type: 'answer', sdp: data }))
		else if (kind === 'ice' && data)
			step('ice', () => (pc.remoteDescription ? addCandidate(data) : pending.push(data)))
	}

	/** False when `label` is not open (the caller relays instead). */
	const send = (label, msg) => {
		const ch = channels[label]
		if (closed || ch?.readyState !== 'open') return false
		ch.send(JSON.stringify(msg))
		return true
	}

	return { signal, send, close }
}
